import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Eye, Loader2 } from 'lucide-react';
import { fmtDate, fmtDateTime } from '../../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(n || 0);

const Field = ({ label, children, wide, testId }) => (
  <div className={wide ? 'col-span-2' : ''}>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="font-medium text-sm" data-testid={testId}>{children || '-'}</div>
  </div>
);

const ccLabel = (d) => {
  if (d.target_type === 'supplier') return `Proveedor: ${d.target_snapshot?.business_name || '—'}`;
  if (d.target_type === 'resident') return `Residente: ${d.target_snapshot?.full_name || '—'}`;
  return null;
};

const AdjustmentFields = ({ d }) => (
  <>
    <Field label="Tipo">{d.adjustment_type === 'debit'
      ? <Badge className="bg-green-100 text-green-800">DÉBITO (+)</Badge>
      : <Badge className="bg-red-100 text-red-800">CRÉDITO (-)</Badge>}</Field>
    <Field label="Caja/Banco" testId="doc-detail-cashbank">{d.cash_bank_name}</Field>
    <Field label="Concepto" testId="doc-detail-concept">{d.concept_name}</Field>
    <Field label="Cta. Cte. vinculada">{ccLabel(d)}</Field>
  </>
);

const TransferFields = ({ d }) => (
  <>
    <Field label="Origen" testId="doc-detail-origin">{d.origin_cash_bank_name}</Field>
    <Field label="Destino" testId="doc-detail-destination">{d.destination_cash_bank_name}</Field>
  </>
);

const EntryTable = ({ e }) => (
  <div className="rounded-md border" data-testid={`doc-detail-entry-${e.entry_number}`}>
    <div className="flex items-center justify-between px-3 py-2 bg-muted/40 text-xs">
      <span className="font-semibold">Asiento N° {e.entry_number} · {fmtDate(e.date)}</span>
      {e.reverses_entry_id
        ? <Badge variant="outline" className="border-rose-300 text-rose-700 text-[10px]">CONTRA-ASIENTO</Badge>
        : e.is_reversed && <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px]">REVERSADO</Badge>}
    </div>
    <Table>
      <TableHeader><TableRow>
        <TableHead className="text-xs">Cuenta</TableHead>
        <TableHead className="text-xs text-right">Debe</TableHead>
        <TableHead className="text-xs text-right">Haber</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {(e.lines || []).map((l, i) => (
          <TableRow key={i}>
            <TableCell className="text-xs"><span className="font-mono text-muted-foreground mr-2">{l.account_code}</span>{l.account_name}</TableCell>
            <TableCell className="text-xs text-right">{l.debit > 0 ? money(l.debit) : ''}</TableCell>
            <TableCell className="text-xs text-right">{l.credit > 0 ? money(l.credit) : ''}</TableCell>
          </TableRow>
        ))}
        <TableRow className="font-semibold">
          <TableCell className="text-xs">Totales</TableCell>
          <TableCell className="text-xs text-right">{money(e.total_debit)}</TableCell>
          <TableCell className="text-xs text-right">{money(e.total_credit)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);

const DetailBody = ({ kind, data }) => {
  const d = data.document;
  const isAdj = kind === 'adjustments';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Número" testId="doc-detail-number"><span className="font-mono">{isAdj ? d.adjustment_number : d.transfer_number}</span></Field>
        <Field label="Monto" testId="doc-detail-amount">{money(d.amount)}</Field>
        <Field label="Fecha">{fmtDate(d.date)}</Field>
        <Field label="Cargado el">{fmtDateTime(d.input_at || d.created_at)}</Field>
        <Field label="Usuario" testId="doc-detail-user">{data.created_by_name}</Field>
        {isAdj ? <AdjustmentFields d={d} /> : <TransferFields d={d} />}
        <Field label="Descripción" wide testId="doc-detail-description">{d.description}</Field>
      </div>
      {d.deleted_at && (
        <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 p-3 text-xs text-rose-900 dark:text-rose-200" data-testid="doc-detail-deleted-notice">
          <b>{isAdj ? 'Ajuste ELIMINADO' : 'Transferencia ELIMINADA'}</b> el {fmtDateTime(d.deleted_at)}.
          {d.deleted_by_name && <span className="block mt-0.5">Eliminado por {d.deleted_by_name}.</span>}
          {d.deletion_reason && <span className="block mt-0.5">Motivo: {d.deletion_reason}</span>}
        </div>
      )}
      <div className="space-y-2 pt-2 border-t" data-testid="doc-detail-entries">
        <h4 className="text-sm font-semibold">Asiento contable</h4>
        {data.journal_entries.length === 0
          ? <p className="text-xs text-muted-foreground">Sin asiento contable vinculado.</p>
          : data.journal_entries.map(e => <EntryTable key={e.entry_id} e={e} />)}
      </div>
    </div>
  );
};

export function TreasuryDocDetailDialog({ target, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!target) { setData(null); return; }
    axios.get(`${BACKEND_URL}/api/treasury/${target.kind}/${target.id}/detail`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [target]);
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="doc-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            {target?.kind === 'adjustments' ? 'Consulta de Ajuste' : 'Consulta de Transferencia'}
          </DialogTitle>
        </DialogHeader>
        {!data && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {data?.error && <p className="text-sm text-rose-600" data-testid="doc-detail-error">No se pudo cargar el detalle.</p>}
        {target && data?.document && <DetailBody kind={target.kind} data={data} />}
      </DialogContent>
    </Dialog>
  );
}
